import { useAuth } from '@clerk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import {
  getPortalIntake,
  savePortalIntakeDraft,
  submitPortalIntake,
  type IntakeAnswers,
  type IntakeAnswerValue,
  type IntakeQuestion,
} from '@/api/portalIntake';
import { IntakeInput } from '@/components/IntakeInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { groupBySection, isAnswered } from '@/lib/portalIntake';

const SAVE_DEBOUNCE_MS = 800;

/** The intake questionnaire on the Onboarding tab: questions grouped by
 *  section, answers saved as you go (debounced), and an explicit
 *  "Mark complete" that stamps `submitted_at` server-side. */
export function PortalIntakeCard({ engagementId }: { engagementId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const intake = useQuery({
    queryKey: ['portal', 'intake', engagementId],
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return getPortalIntake(engagementId, token);
    },
  });

  // Local answers overlay the server payload so typing stays snappy;
  // pending edits accumulate and flush as one debounced PUT.
  const [local, setLocal] = useState<IntakeAnswers>({});
  const pending = useRef<IntakeAnswers>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const save = useMutation({
    mutationFn: async (answers: IntakeAnswers) => {
      const token = (await getToken()) ?? '';
      return savePortalIntakeDraft(engagementId, answers, token);
    },
    onSuccess: (data) => {
      setSaveState('saved');
      qc.setQueryData(['portal', 'intake', engagementId], data);
    },
    onError: () => setSaveState('error'),
  });

  const submit = useMutation({
    mutationFn: async () => {
      const token = (await getToken()) ?? '';
      return submitPortalIntake(engagementId, token);
    },
    onSuccess: (data) => {
      qc.setQueryData(['portal', 'intake', engagementId], data);
      qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
    },
  });

  const { mutate: saveMutate } = save;
  useEffect(() => {
    // Flush any debounce-buffered edits on unmount so navigating away
    // right after typing doesn't drop the last answers.
    return () => {
      if (timer.current) clearTimeout(timer.current);
      const batch = pending.current;
      pending.current = {};
      if (Object.keys(batch).length > 0) saveMutate(batch);
    };
  }, [saveMutate]);

  const setAnswer = (id: string, value: IntakeAnswerValue) => {
    setLocal((prev) => ({ ...prev, [id]: value }));
    pending.current = { ...pending.current, [id]: value };
    setSaveState('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const batch = pending.current;
      pending.current = {};
      save.mutate(batch);
    }, SAVE_DEBOUNCE_MS);
  };

  if (intake.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>About your business</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-brand-slate">Loading questionnaire…</CardContent>
      </Card>
    );
  }
  if (intake.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>About your business</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-destructive">
          Couldn&apos;t load the questionnaire. Refresh to try again.
        </CardContent>
      </Card>
    );
  }

  const data = intake.data;
  const answers: IntakeAnswers = { ...data.answers, ...local };
  const sections = groupBySection(data.intake_schema.questions);
  const missingRequired = data.intake_schema.questions.filter(
    (question) => question.required && !isAnswered(answers[question.id]),
  );
  const submitted = data.submitted_at != null;
  const settled = saveState !== 'saving' && !save.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>About your business</CardTitle>
        <CardDescription>
          A few questions that shape the engagement. Answers save as you go — mark it complete
          when you&apos;re done. You can still edit afterwards.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {sections.map((section) => (
          <fieldset key={section.section} className="grid gap-3">
            <legend className="text-sm font-semibold text-brand-navy">{section.section}</legend>
            {section.questions.map((question) => (
              <IntakeQuestionField
                key={question.id}
                question={question}
                value={answers[question.id]}
                onChange={(value) => setAnswer(question.id, value)}
              />
            ))}
          </fieldset>
        ))}

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <Button
            type="button"
            size="sm"
            onClick={() => submit.mutate()}
            disabled={missingRequired.length > 0 || submit.isPending || !settled}
          >
            {submit.isPending ? 'Submitting…' : submitted ? 'Re-submit' : 'Mark complete'}
          </Button>
          <span className="text-xs text-brand-slate" role="status">
            {saveState === 'saving' || save.isPending
              ? 'Saving…'
              : saveState === 'error'
                ? "Couldn't save — check your connection."
                : submitted
                  ? `Completed ${new Date(data.submitted_at ?? '').toLocaleDateString()}`
                  : missingRequired.length > 0
                    ? `${missingRequired.length} required question${missingRequired.length === 1 ? '' : 's'} left`
                    : saveState === 'saved'
                      ? 'Draft saved'
                      : 'Ready to submit'}
          </span>
          {submit.isError && (
            <span className="text-xs text-destructive">
              {submit.error instanceof ApiError
                ? `Couldn't submit (HTTP ${submit.error.status}).`
                : "Couldn't submit."}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function IntakeQuestionField({
  question,
  value,
  onChange,
}: {
  question: IntakeQuestion;
  value: IntakeAnswerValue | undefined;
  onChange: (value: IntakeAnswerValue) => void;
}) {
  const inputId = `intake-${question.id}`;
  const usesNativeInput = !['boolean', 'multi_select'].includes(question.answer_type);
  return (
    <div className="grid gap-1">
      <label
        id={`${inputId}-label`}
        htmlFor={usesNativeInput ? inputId : undefined}
        className="text-sm text-brand-navy"
      >
        {question.prompt}
        {question.required && <span className="text-destructive"> *</span>}
      </label>
      <IntakeInput inputId={inputId} question={question} value={value} onChange={onChange} />
    </div>
  );
}
